import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Text } from "@/components/podkit/typography/Text";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { type PlainAgentExecution } from "@/queries/agent-queries";
import { useStartEnvironment } from "@/queries/environment-queries";
import { getDetailsURL } from "@/routes/environments/details-url";
import { formatError } from "@/utils/errors";
import { getActiveEnvironmentId } from "@/utils/agent-executions";
import type { FC } from "react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

type SupportBundleUnavailableModalProps = {
    open: boolean;
    onClose: () => void;
    agentExecution: PlainAgentExecution;
};

export const SupportBundleUnavailableModal: FC<SupportBundleUnavailableModalProps> = ({
    open,
    onClose,
    agentExecution,
}) => {
    const [isCreating, setIsCreating] = useState(false);
    const navigate = useNavigate();
    const { toast } = useToast();
    const startEnvironment = useStartEnvironment();

    const onOpenChange = (open: boolean) => {
        if (!open && !isCreating) {
            onClose();
        }
    };

    const handleStartEnvironment = useCallback(async () => {
        if (isCreating) return;

        setIsCreating(true);
        try {
            // Get the environment ID from the agent execution context
            const environmentId = getActiveEnvironmentId(agentExecution);

            if (!environmentId) {
                toast({
                    title: "Unable to start environment",
                    description: "No environment associated with this conversation",
                });
                return;
            }

            // Start the environment
            const environment = await startEnvironment.mutateAsync({ environmentId, skipPolicyCheck: true });

            // Navigate to the environment details page
            if (environment) {
                navigate(getDetailsURL({ environment }));
            }

            onClose();
        } catch (error) {
            toast({
                title: "Failed to start environment",
                description: formatError(error),
            });
        } finally {
            setIsCreating(false);
        }
    }, [isCreating, agentExecution, startEnvironment, toast, navigate, onClose]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-lg"
                data-track-location={TrackLocations.SupportBundleUnavailableModal}
                data-testid="support-bundle-unavailable-modal"
            >
                <DialogHeader>
                    <DialogTitle>Support bundle requires running environment</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <DialogBody>
                    <Text>Support bundles are only available when the environment is running.</Text>
                    <Text className="mt-4 text-content-secondary">
                        Please restart your environment and access the support bandle again.
                    </Text>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            disabled={isCreating}
                            data-tracking-id="cancel-support-bundle-unavailable-modal"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="button"
                        variant="primary"
                        onClick={handleStartEnvironment}
                        autoFocus={true}
                        loading={isCreating}
                        disabled={isCreating}
                        data-tracking-id="start-environment-support-bundle-unavailable-modal"
                    >
                        Start Environment
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
